import { useState, useEffect } from 'react';
import { X, Search, FileText, Check } from 'lucide-react';

/**
 * SelectorComprobantes - Componente modal para seleccionar comprobantes (facturas)
 * con selección múltiple e imputación de montos
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está abierto
 * @param {Function} props.onClose - Callback al cerrar el modal
 * @param {Function} props.onConfirm - Callback al confirmar selección (recibe array de {comprobanteId, monto})
 * @param {string} props.entidadId - ID del cliente o proveedor para filtrar comprobantes
 * @param {'cliente'|'proveedor'} props.mode - Modo de selección
 * @param {Array} props.selected - Array de imputaciones ya seleccionadas
 */
export default function SelectorComprobantes({
  isOpen,
  onClose,
  onConfirm,
  entidadId,
  mode = 'proveedor',
  selected = [],
}) {
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});

  // TODO: Reemplazar con useQuery de GraphQL cuando esté disponible
  // const [result] = useQuery({
  //   query: mode === 'cliente' ? GET_FACTURAS_CLIENTE : GET_FACTURAS_PROVEEDOR,
  //   variables: { entidadId, pendientes: true },
  // });

  // Datos dummy - Comprobantes del proveedor/cliente
  const dummyComprobantes = entidadId
    ? [
        {
          id: 'fc1',
          numero: 'FC-0001-00012345',
          fecha: '2025-10-15',
          total: 250000.0,
          saldo: 250000.0,
          moneda: 'ARS',
        },
        {
          id: 'fc2',
          numero: 'FC-0001-00067890',
          fecha: '2025-10-20',
          total: 100000.0,
          saldo: 50000.0,
          moneda: 'ARS',
        },
        {
          id: 'fc3',
          numero: 'FC-0001-00067891',
          fecha: '2025-10-25',
          total: 175000.0,
          saldo: 175000.0,
          moneda: 'ARS',
        },
      ]
    : [];

  useEffect(() => {
    // Inicializar items seleccionados desde props
    const initialSelected = {};
    selected.forEach((item) => {
      initialSelected[item.comprobanteId] = {
        ...item,
        monto: item.monto || 0,
      };
    });
    setSelectedItems(initialSelected);
  }, [selected]);

  useEffect(() => {
    if (search) {
      const filtered = dummyComprobantes.filter((item) =>
        item.numero.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(dummyComprobantes);
    }
  }, [search, entidadId]);

  const handleToggleSelect = (comprobante) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      if (newSelected[comprobante.id]) {
        delete newSelected[comprobante.id];
      } else {
        newSelected[comprobante.id] = {
          comprobanteId: comprobante.id,
          comprobanteNumero: comprobante.numero,
          monto: comprobante.saldo,
          saldoAnterior: comprobante.saldo,
          total: comprobante.total,
          fecha: comprobante.fecha,
          moneda: comprobante.moneda,
        };
      }
      return newSelected;
    });
  };

  const handleMontoChange = (comprobanteId, value) => {
    const numValue = parseFloat(value) || 0;
    setSelectedItems((prev) => ({
      ...prev,
      [comprobanteId]: {
        ...prev[comprobanteId],
        monto: numValue,
      },
    }));
  };

  const handleConfirm = () => {
    const imputaciones = Object.values(selectedItems).filter(
      (item) => item.monto > 0
    );
    onConfirm(imputaciones);
    onClose();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const totalImputado = Object.values(selectedItems).reduce(
    (sum, item) => sum + (item.monto || 0),
    0
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="selector-comprobantes-title"
    >
      <div
        className="max-w-5xl w-full mx-4 rounded-2xl border bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[rgb(var(--border))]">
          <h2
            id="selector-comprobantes-title"
            className="text-lg font-semibold text-[rgb(var(--fg))] flex items-center gap-2"
          >
            <FileText className="w-5 h-5" aria-hidden="true" />
            Seleccionar Comprobantes
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-[rgba(var(--muted))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-[rgb(var(--border))]">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]"
              aria-hidden="true"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número de comprobante..."
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              aria-label="Buscar comprobante"
            />
          </div>
          {!entidadId && (
            <p className="mt-2 text-sm text-[rgb(var(--muted))]">
              Seleccione primero un {mode === 'cliente' ? 'cliente' : 'proveedor'}{' '}
              para ver sus comprobantes.
            </p>
          )}
        </div>

        {/* Table */}
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
              <tr>
                <th className="px-4 py-3 w-12"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Número
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Total
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Saldo
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Monto a Imputar
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--border))]">
              {filteredData.length > 0 ? (
                filteredData.map((comprobante) => {
                  const isSelected = !!selectedItems[comprobante.id];
                  return (
                    <tr
                      key={comprobante.id}
                      className={`hover:bg-[rgba(var(--muted),0.3)] transition-colors ${
                        isSelected ? 'bg-[rgba(var(--brand),0.1)]' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleSelect(comprobante)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                            isSelected
                              ? 'bg-[rgb(var(--brand))] border-[rgb(var(--brand))]'
                              : 'border-[rgb(var(--border))] hover:border-[rgb(var(--brand))]'
                          }`}
                          aria-label={`${isSelected ? 'Deseleccionar' : 'Seleccionar'} comprobante ${comprobante.numero}`}
                          aria-pressed={isSelected}
                        >
                          {isSelected && (
                            <Check className="w-4 h-4 text-white" aria-hidden="true" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-[rgb(var(--fg))] font-medium">
                        {comprobante.numero}
                      </td>
                      <td className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
                        {new Date(comprobante.fecha).toLocaleDateString('es-AR')}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-[rgb(var(--fg))]">
                        {formatCurrency(comprobante.total)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-[rgb(var(--fg))]">
                        {formatCurrency(comprobante.saldo)}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          max={comprobante.saldo}
                          step="0.01"
                          value={selectedItems[comprobante.id]?.monto || ''}
                          onChange={(e) =>
                            handleMontoChange(comprobante.id, e.target.value)
                          }
                          disabled={!isSelected}
                          className="w-32 ml-auto px-3 py-1.5 text-sm text-right rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                          aria-label={`Monto a imputar para ${comprobante.numero}`}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[rgb(var(--muted))]">
                    {entidadId
                      ? 'No hay comprobantes pendientes'
                      : 'Seleccione una entidad para ver comprobantes'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[rgb(var(--border))] flex items-center justify-between">
          <div className="text-sm">
            <span className="text-[rgb(var(--muted))]">Total a imputar: </span>
            <span className="font-semibold text-[rgb(var(--fg))]">
              {formatCurrency(totalImputado)}
            </span>
            <span className="ml-2 text-[rgb(var(--muted))]">
              ({Object.keys(selectedItems).length} comprobante
              {Object.keys(selectedItems).length !== 1 ? 's' : ''})
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={Object.keys(selectedItems).length === 0}
              className="px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Confirmar Selección
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
